import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './AccountSettings.css';

const AccountSettings: React.FC = () => {
  const { user, updateProfile, changePassword, deleteAccount } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'account' | 'password' | 'delete'>('account');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Email change modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChangeStep, setEmailChangeStep] = useState<'request' | 'verify'>('request');

  // Account form state
  const [accountForm, setAccountForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Delete account confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleAccountUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Sadece isim değiştiyse direkt güncelle
    if (accountForm.name !== user?.name && accountForm.email === user?.email) {
      setIsLoading(true);
      try {
        await updateProfile(accountForm);
        setMessage({ type: 'success', text: 'Hesap bilgileriniz başarıyla güncellendi!' });
      } catch (error: any) {
        setMessage({ type: 'error', text: error.message || 'Güncelleme başarısız oldu.' });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Email değişiyorsa modal aç
    if (accountForm.email !== user?.email) {
      setNewEmail(accountForm.email);
      setShowEmailModal(true);
      setEmailChangeStep('request');
    }
  };

  const handleRequestEmailChange = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      await authAPI.requestEmailChange(newEmail);
      setEmailChangeStep('verify');
      setMessage({ type: 'success', text: 'Doğrulama kodu eski email adresinize gönderildi!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Kod gönderilemedi.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      await authAPI.verifyEmailChange(verificationCode, newEmail);
      setMessage({ type: 'success', text: 'Email adresiniz başarıyla güncellendi!' });

      // Kullanıcı bilgilerini güncelle
      if (accountForm.name !== user?.name) {
        await updateProfile({ name: accountForm.name, email: newEmail });
      }

      setTimeout(() => {
        setShowEmailModal(false);
        setVerificationCode('');
        window.location.reload(); // Sayfayı yenile
      }, 2000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Doğrulama başarısız.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'Yeni şifreler eşleşmiyor!' });
      setIsLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Yeni şifre en az 6 karakter olmalıdır!' });
      setIsLoading(false);
      return;
    }

    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setMessage({ type: 'success', text: 'Şifreniz başarıyla değiştirildi!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Şifre değiştirme başarısız oldu.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'SİL') {
      setMessage({ type: 'error', text: 'Lütfen "SİL" yazarak onaylayın.' });
      return;
    }

    if (!window.confirm('Hesabınız kalıcı olarak silinecek. Emin misiniz?')) {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await deleteAccount();
      navigate('/');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Hesap silme başarısız oldu.' });
      setIsLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="account-settings-page">
      {/* Hero Header */}
      <div className="account-header">
        <div className="account-header-content">
          <div className="account-avatar">
            <i className="bi bi-person-circle"></i>
          </div>
          <div className="account-user-info">
            <h1 className="account-title">Hesap Ayarları</h1>
            <p className="account-subtitle">{user.name}</p>
            <p className="account-email">{user.email}</p>
            <span className="account-role-badge">
              <i className="bi bi-shield-check"></i>
              {user.role?.role_name === 'admin' ? 'Yönetici' :
               user.role?.role_name === 'lawyer' ? 'Avukat' : 'Kullanıcı'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="account-container">
        {/* Tabs Navigation */}
        <div className="account-tabs">
          <button
            className={`account-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <i className="bi bi-person"></i>
            <span>Hesap Bilgileri</span>
          </button>
          <button
            className={`account-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <i className="bi bi-lock"></i>
            <span>Şifre Değiştir</span>
          </button>
          <button
            className={`account-tab ${activeTab === 'delete' ? 'active' : ''}`}
            onClick={() => setActiveTab('delete')}
          >
            <i className="bi bi-exclamation-triangle"></i>
            <span>Hesabı Sil</span>
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`account-message ${message.type}`}>
            <i className={`bi bi-${message.type === 'success' ? 'check-circle-fill' : 'exclamation-circle-fill'}`}></i>
            <span>{message.text}</span>
          </div>
        )}

        {/* Tab Content */}
        <div className="account-content">
          {/* Account Info Tab */}
          {activeTab === 'account' && (
            <div className="account-card">
              <h2 className="card-title">
                <i className="bi bi-person-lines-fill"></i>
                Hesap Bilgilerini Güncelle
              </h2>
              <p className="card-description">
                Ad, soyad ve e-posta adresinizi güncelleyebilirsiniz.
              </p>

              <form onSubmit={handleAccountUpdate} className="account-form">
                <div className="form-group">
                  <label htmlFor="name">
                    <i className="bi bi-person"></i>
                    Ad Soyad
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={accountForm.name}
                    onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                    placeholder="Adınız ve soyadınız"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="bi bi-envelope"></i>
                    E-posta Adresi
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={accountForm.email}
                    onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                    placeholder="email@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" className="btn-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-repeat spin"></i>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i>
                      Değişiklikleri Kaydet
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="account-card">
              <h2 className="card-title">
                <i className="bi bi-shield-lock"></i>
                Şifre Değiştir
              </h2>
              <p className="card-description">
                Güvenliğiniz için güçlü bir şifre kullanın (en az 6 karakter).
              </p>

              <form onSubmit={handlePasswordChange} className="account-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">
                    <i className="bi bi-key"></i>
                    Mevcut Şifre
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Mevcut şifreniz"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">
                    <i className="bi bi-lock"></i>
                    Yeni Şifre
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="En az 6 karakter"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    <i className="bi bi-lock-fill"></i>
                    Yeni Şifre (Tekrar)
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Yeni şifrenizi tekrar girin"
                    required
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" className="btn-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-repeat spin"></i>
                      Değiştiriliyor...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check"></i>
                      Şifreyi Değiştir
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Delete Account Tab */}
          {activeTab === 'delete' && (
            <div className="account-card danger">
              <h2 className="card-title danger">
                <i className="bi bi-exclamation-triangle-fill"></i>
                Tehlikeli Alan - Hesabı Sil
              </h2>
              <p className="card-description danger">
                Bu işlem geri alınamaz! Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.
              </p>

              <div className="account-form">
                <div className="form-group">
                  <label htmlFor="deleteConfirm">
                    Onaylamak için <strong>"SİL"</strong> yazın
                  </label>
                  <input
                    type="text"
                    id="deleteConfirm"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="SİL"
                    className="delete-input"
                    disabled={isLoading}
                  />
                </div>

                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleDeleteAccount}
                  disabled={isLoading || deleteConfirmation !== 'SİL'}
                >
                  {isLoading ? (
                    <>
                      <i className="bi bi-arrow-repeat spin"></i>
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-trash"></i>
                      Hesabı Kalıcı Olarak Sil
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email Change Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="bi bi-envelope-at"></i>
                Email Değiştirme
              </h3>
              <button onClick={() => setShowEmailModal(false)} className="modal-close">
                <i className="bi bi-x"></i>
              </button>
            </div>

            <div className="modal-body">
              {emailChangeStep === 'request' ? (
                <>
                  <p className="modal-description">
                    Email adresinizi değiştirmek için eski email adresinize ({user?.email}) doğrulama kodu göndereceğiz.
                  </p>
                  <div className="email-preview">
                    <strong>Yeni Email:</strong> {newEmail}
                  </div>
                  <button
                    onClick={handleRequestEmailChange}
                    disabled={isLoading}
                    className="btn-submit"
                  >
                    {isLoading ? 'Gönderiliyor...' : 'Doğrulama Kodu Gönder'}
                  </button>
                </>
              ) : (
                <>
                  <p className="modal-description">
                    <strong>{user?.email}</strong> adresine gönderilen 6 haneli doğrulama kodunu girin:
                  </p>
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="verification-input"
                    autoFocus
                  />
                  <button
                    onClick={handleVerifyEmailChange}
                    disabled={isLoading || verificationCode.length !== 6}
                    className="btn-submit"
                  >
                    {isLoading ? 'Doğrulanıyor...' : 'Onayla'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
